/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import GET_MY_BOTS from "src/graphql/query/chatbot/getMyBots";
import DELETE_BOT from "src/graphql/mutation/chatbot/deleteBot";
import DeleteDialog from "src/components/shared/atoms/dialog";
import BinIcon from "src/assets/images/settings/deleteIcon.png";
import useStyles from "./Styles";

const ChatbotWidgets: React.FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, refetch } = useQuery(GET_MY_BOTS, {
    fetchPolicy: "network-only",
  });

  const [deleteBotMutation, { loading: deleteLoading }] =
    useMutation(DELETE_BOT);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [botToDelete, setBotToDelete] = useState<string | null>(null);

  const bots = data?.getMyBots?.bots || [];

  const handleDeleteClick = (id: string) => {
    setBotToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (botToDelete) {
      try {
        const { data: deleteData } = await deleteBotMutation({
          variables: { botId: botToDelete },
        });

        if (deleteData?.deleteBot?.success) {
          enqueueSnackbar("Bot deleted successfully", { variant: "success" });
          refetch();
        } else {
          enqueueSnackbar(
            deleteData?.deleteBot?.error || "Failed to delete bot",
            {
              variant: "error",
            },
          );
        }
      } catch (error) {
        enqueueSnackbar("Error deleting bot", { variant: "error" });
      } finally {
        setBotToDelete(null);
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBotToDelete(null);
  };

  return (
    <Box className={classes.root}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" className={classes.boxText} gutterBottom>
          Chatbot Widgets
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Create custom chatbot widgets for your websites and landing pages.
          Each widget has its own knowledge base and appearance settings.
        </Typography>
      </Box>

      {loading ? (
        <Typography>Loading bots...</Typography>
      ) : bots.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Bots Yet
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Create your first AI bot to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/settings/chatbot-widgets/create")}
            sx={{ mt: 2 }}
          >
            Create Your First Bot
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Create new bot card */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: 4,
                "&:hover": {
                  borderColor: "secondary.main",
                  bgcolor: "rgba(107, 233, 0, 0.04)",
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                },
                height: "100%",
                display: "flex",
              }}
              onClick={() => navigate("/settings/chatbot-widgets/create")}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  minHeight: 220,
                  textAlign: "center",
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "action.hover",
                    mb: 2,
                    border: "2px dashed",
                    borderColor: "divider",
                  }}
                >
                  <AddIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Create New Bot
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ px: 2 }}
                >
                  Build a custom AI assistant for your team
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {bots.map((bot: any) => (
            <Grid item xs={12} sm={6} md={4} key={bot.botId}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "visible",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                    borderColor: "secondary.main",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2.5,
                    }}
                  >
                    <Avatar
                      src={bot.launcherImageUrl}
                      sx={{
                        width: 54,
                        height: 54,
                        bgcolor: bot.launcherImageUrl
                          ? "transparent"
                          : "secondary.main",
                        color: "white",
                        boxShadow: bot.launcherImageUrl
                          ? "none"
                          : "0 4px 10px rgba(107, 233, 0, 0.3)",
                        border: bot.launcherImageUrl ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      {!bot.launcherImageUrl && (
                        <SmartToyIcon sx={{ fontSize: 32 }} />
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        noWrap
                        title={bot.name}
                        sx={{ mb: 0.5 }}
                      >
                        {bot.name || "Untitled Bot"}
                      </Typography>
                      <Chip
                        label={bot.enabled ? "Active" : "Disabled"}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          bgcolor: bot.enabled
                            ? "success.light"
                            : "action.disabledBackground",
                          color: bot.enabled ? "white" : "text.secondary",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        rowGap: 1,
                      }}
                    >
                      <Chip
                        label={`${bot.deploymentCount || 0} Deployments`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${bot.knowledgeSourceCount || 0} Sources`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Added on {new Date(bot.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
                <Box sx={{ px: 2, pb: 2, pt: 1, display: "flex", gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() =>
                      navigate(`/settings/chatbot-widgets/${bot.botId}`)
                    }
                    sx={{ borderRadius: 2, py: 1 }}
                  >
                    Configure
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleDeleteClick(bot.botId)}
                    sx={{ borderRadius: 2, minWidth: 44 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        modalClose={handleCloseDeleteDialog}
        actionHandler={handleConfirmDelete}
        text="Are you sure you want to delete this widget? This action cannot be undone."
        btnText="Delete"
        loader={deleteLoading}
        img={BinIcon}
      />
    </Box>
  );
};

export default ChatbotWidgets;
